import { useRelativeTime } from "@/hooks";
import type { Project } from "@/types/stores/database";
import {
  Badge,
  Button,
  Card,
  Flex,
  Group,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconArrowRight,
  IconBook2,
  IconClock,
  IconFileSpreadsheet,
  IconFolder,
} from "@tabler/icons-react";
import { Link } from "react-router";

function ProjectCard({
  project,
}: {
  project: Project;
}) {
  const lastUpdated = (project as any).updated_at || project.created_at;
  const timeAgo = useRelativeTime({
    date: lastUpdated,
    prefix: "Updated ",
    fallback: "Recently updated",
  });

  // Get the most recently updated dataflow
  const mostRecentDataflow = project.dataflows?.sort((a, b) => {
    const dateA = new Date(
      a.datetime_updated || a.datetime_created || 0
    ).getTime();
    const dateB = new Date(
      b.datetime_updated || b.datetime_created || 0
    ).getTime();
    return dateB - dateA;
  })[0];

  // Determine the link - use most recent dataflow if available, otherwise first dataflow, or just project
  const dataflowId =
    mostRecentDataflow?.dataflow_id || project.dataflows?.[0]?.dataflow_id;
  const linkTo = dataflowId
    ? `/build/${project.id}/${dataflowId}`
    : `/build/${project.id}`;

  return (
    <Card
      p={0}
      shadow="lg"
      radius="md"
      component={Link}
      to={linkTo}
      style={{
        textDecoration: "none",
        color: "inherit",
      }}
    >
      <Group
        p="lg"
        gap="md"
        align="flex-start"
        justify="space-between"
        style={{
          borderLeft: "6px solid var(--mantine-color-eu_flag-filled)",
        }}
      >
        <Group gap="md" align="flex-start" flex={1}>
          <Stack flex={1}>
            <Group gap={8} align="center">
              <Text fw={600} fz={18} truncate>
                {project.title}
              </Text>
              {project.dataflows && project.dataflows.length > 0 && (
                <Badge size="md" variant="light" color="blue">
                  {project.dataflows.length}
                </Badge>
              )}
            </Group>
            <Flex direction="column" gap={4}>
              {mostRecentDataflow?.dataflow_name && (
                <Group gap={4} align="center">
                  <IconBook2
                    size={14}
                    style={{ color: "var(--mantine-color-gray-9)" }}
                  />
                  <Text c="gray.9" fz={14}>
                    {mostRecentDataflow.dataflow_name}
                  </Text>
                </Group>
              )}
              {project.description && (
                <Text c="gray.9" fz={14} lineClamp={2}>
                  {project.description}
                </Text>
              )}
              <Group gap="xs" mt={4}>
                <IconClock
                  size={14}
                  style={{ color: "var(--mantine-color-gray-9)" }}
                />
                <Text size="sm" c="gray.9">
                  {timeAgo}
                </Text>
              </Group>
            </Flex>
          </Stack>
        </Group>
        <IconArrowRight
          size={20}
          style={{ color: "var(--mantine-color-gray-9)" }}
        />
      </Group>
    </Card>
  );
}

export function RecentWork({
  projects,
}: {
  projects: Project[];
}) {
  const recentProjects = projects
    .sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return dateB - dateA;
    })
    .slice(0, 3);

  if (recentProjects.length === 0) {
    return (
      <Stack gap="xl">
        <Group gap="sm" align="center">
          <ThemeIcon size={32} radius="md" color="blue" variant="light">
            <IconFileSpreadsheet size={20} />
          </ThemeIcon>
          <Title order={2} fw={600} fz={28}>
            Recent Work
          </Title>
        </Group>
        <Card p="xl" shadow="lg" radius="md">
          <Stack gap="md" align="center">
            <Text c="gray.9" fz={16} ta="center">
              You haven't created any projects yet. Start by creating your first
              project to begin working with data.
            </Text>
            <Button
              component={Link}
              to="/projects"
              leftSection={<IconFolder size={16} />}
            >
              Create Your First Project
            </Button>
          </Stack>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack gap="xl">
      <Group gap="sm" align="center" justify="space-between">
        <Group gap="sm" align="center">
          <Title order={2} fw={600} fz={28}>
            Recent Work
          </Title>
        </Group>
        <Button
          component={Link}
          to="/build"
          variant="subtle"
          rightSection={<IconArrowRight size={16} />}
        >
          View All Projects
        </Button>
      </Group>
      <Text c="gray.9" fz={16} lh={1.6}>
        Quickly access your recently worked on projects. Pick up where you left
        off and continue your data analysis.
      </Text>
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
        {recentProjects.map((project) => (
          <ProjectCard key={project.id} project={project} />
        ))}
      </SimpleGrid>
    </Stack>
  );
}
